// "Add digital employee" (DE-WIZARD): the steps' bodies, shared by the create flow and the
// profile's edit mode. Each is a section card on the §6.9 Wizard template that edits part of
// the form and makes no call: the flow (add-de-wizard.tsx) or the edit mode (edit-de.tsx) saves.
import { BACKSTORY_SHAPE_HINT } from "@companyos/schema/contracts";
import {
  Badge,
  Button,
  Checkbox,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  Label,
  NativeSelect,
  NativeSelectOption,
  Notice,
  SettingsSection,
  Textarea,
} from "@companyos/ui";
import {
  BotIcon,
  GaugeIcon,
  LayoutTemplateIcon,
  MessagesSquareIcon,
  PlusIcon,
  ScrollTextIcon,
  Trash2Icon,
  WalletIcon,
} from "lucide-react";
import { type ReactNode, useId, useState } from "react";
import type { MetricDef } from "../../../api/types";
import { useOperatorName } from "../../operator-name";
import { UnverifiedBadge } from "../unverified-badge";
import {
  DUTY_LABELS,
  type Duty,
  type Form,
  type KpiRow,
  type ModelRow,
  modelRow,
  NEW_SEAT,
  type TemplateChoice,
  targetValid,
} from "./model";

export type ChartSeat = {
  id: string;
  title: string;
  status: string;
  supervisor_seat_id: string | null;
  holders: { membership_id: string; membership_kind: string; display_name: string }[];
};

/** "Senior Recruiter · Priya Shah" / "Recruiter · open" (codex: a filled seat is not "free"). */
export const seatLabel = (s: ChartSeat) => {
  const people = s.holders.filter((h) => h.membership_kind === "person");
  return `${s.title} · ${people.length ? people.map((h) => h.display_name).join(", ") : "open"}`;
};
export const hasPerson = (s: ChartSeat | undefined) =>
  Boolean(s?.holders.some((h) => h.membership_kind === "person"));
export const hasDe = (s: ChartSeat) =>
  s.holders.some((h) => h.membership_kind === "digital_employee");

// 1. Template -----------------------------------------------------------------------------------

export function TemplateStep({
  choices,
  value,
  onPick,
  note,
}: {
  choices: TemplateChoice[];
  value: string;
  onPick: (c: TemplateChoice) => void;
  /** Why "Deploy it yourself" is not offered (null: it is). */
  note: ReactNode;
}) {
  const operator = useOperatorName();
  const group = (
    title: string,
    hint: string,
    list: TemplateChoice[],
    slot: string | undefined = list[0]?.path,
  ) =>
    list.length ? (
      <fieldset className="flex flex-col gap-2.5" data-slot={`templates-${slot}`}>
        <legend className="mb-1 text-sm font-medium">
          {title} <span className="font-normal text-muted-foreground">· {hint}</span>
        </legend>
        {list.map((c) => (
          <label
            key={c.id}
            data-template={c.id}
            className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-4 has-[:checked]:border-primary has-[:checked]:bg-primary-soft/40"
          >
            <input
              type="radio"
              name="de-template"
              className="mt-1 accent-primary"
              checked={value === c.id}
              onChange={() => onPick(c)}
              aria-label={`${c.name} (${c.path === "self_serve" ? "deploy it yourself" : `${operator} sets it up`})`}
            />
            <span className="flex min-w-0 flex-col gap-1">
              <span className="inline-flex flex-wrap items-center gap-1.5 font-medium">
                {c.name}
                {c.unverified ? <UnverifiedBadge /> : null}
                {c.path === "self_serve" && !c.unverified ? (
                  <Badge variant="success">Verified</Badge>
                ) : null}
              </span>
              <span className="text-sm text-muted-foreground">{c.hint}</span>
            </span>
          </label>
        ))}
      </fieldset>
    ) : null;
  return (
    <SettingsSection
      icon={LayoutTemplateIcon}
      title="Template"
      description="Start from a template, blank, or define it yourself."
    >
      {/* WF-POLISH: Custom, defined right here (its own group, first). */}
      {group(
        "Define it yourself",
        "name, role, goal, backstory",
        choices.filter((c) => c.custom),
      )}
      {group(
        "Deploy it yourself",
        "the owner or an admin, no approval",
        choices.filter((c) => c.path === "self_serve"),
      )}
      {note ? (
        <Notice tone="neutral" data-slot="self-serve-note">
          {note}
        </Notice>
      ) : null}
      {/* TEMPLATES-T2: the role templates (prefilled from the role and the company's overlay). */}
      {group(
        "From a role",
        `prefilled, ${operator} stages it`,
        choices.filter((c) => c.roleTemplateId),
        "roles",
      )}
      {group(
        `${operator} sets it up`,
        `${operator} stages it, the owner approves the first deploy`,
        choices.filter((c) => c.path === "done_for_you" && !c.custom && !c.roleTemplateId),
      )}
    </SettingsSection>
  );
}

// 2. Name & seat ---------------------------------------------------------------------------------

export function SeatStep({
  form,
  set,
  seats,
  roles,
  teams,
  locked,
  custom = false,
}: {
  form: Form;
  set: (patch: Partial<Form>) => void;
  seats: ChartSeat[];
  roles: { id: string; name: string }[];
  teams: { id: string; name: string }[];
  locked: boolean;
  /** WF-POLISH: the Custom choice asks for role, goal and backstory here. */
  custom?: boolean;
}) {
  const byId = new Map(seats.map((s) => [s.id, s]));
  const open = seats.filter((s) => s.status !== "closed" && !hasDe(s));
  const staffed = seats.filter((s) => s.status !== "closed" && hasPerson(s));
  const picked = byId.get(form.seat);
  const supervisor = picked?.supervisor_seat_id ? byId.get(picked.supervisor_seat_id) : undefined;
  return (
    <SettingsSection icon={BotIcon} title="Name & seat">
      <Field>
        <FieldLabel htmlFor="add-de-name">Name</FieldLabel>
        <Input
          id="add-de-name"
          autoFocus
          required
          disabled={locked}
          value={form.name}
          onChange={(e) => set({ name: e.target.value })}
        />
        <FieldDescription>
          How it appears in Agents, Chat (@-mentions) and reports.
        </FieldDescription>
      </Field>
      <Field>
        <FieldLabel htmlFor="add-de-seat">Seat</FieldLabel>
        <NativeSelect
          id="add-de-seat"
          disabled={locked}
          value={form.seat}
          onChange={(e) =>
            set({
              seat: e.target.value,
              ...(e.target.value === NEW_SEAT && !form.seatTitle && form.name.trim()
                ? { seatTitle: `${form.name.trim()}'s seat` }
                : {}),
            })
          }
        >
          <NativeSelectOption value="">Choose a seat…</NativeSelectOption>
          {open.map((s) => (
            <NativeSelectOption key={s.id} value={s.id}>
              {seatLabel(s)}
              {s.supervisor_seat_id
                ? ` · reports to ${byId.get(s.supervisor_seat_id)?.title ?? "a seat"}`
                : ""}
            </NativeSelectOption>
          ))}
          <NativeSelectOption value={NEW_SEAT}>A new seat…</NativeSelectOption>
        </NativeSelect>
        <FieldDescription>
          An open seat or one a person holds (the DE works beside them), or a new seat.
        </FieldDescription>
      </Field>
      {picked && !hasPerson(supervisor) ? (
        <Notice tone="warning" data-slot="seat-unsupervised">
          {picked.title}{" "}
          {supervisor
            ? `reports to ${supervisor.title}, which no person holds`
            : "reports to no seat"}
          . A DE needs a person supervising it: pick another seat or a new one.
        </Notice>
      ) : null}
      {form.seat === NEW_SEAT ? (
        <div className="grid gap-5 sm:grid-cols-2" data-slot="add-de-new-seat">
          <Field className="sm:col-span-2">
            <FieldLabel htmlFor="add-de-seat-title">Seat title</FieldLabel>
            <Input
              id="add-de-seat-title"
              disabled={locked}
              value={form.seatTitle}
              onChange={(e) => set({ seatTitle: e.target.value })}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="add-de-seat-role">Role</FieldLabel>
            <NativeSelect
              id="add-de-seat-role"
              disabled={locked}
              value={form.roleId}
              onChange={(e) => set({ roleId: e.target.value })}
            >
              <NativeSelectOption value="">Choose a role…</NativeSelectOption>
              {roles.map((r) => (
                <NativeSelectOption key={r.id} value={r.id}>
                  {r.name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          <Field>
            <FieldLabel htmlFor="add-de-seat-supervisor">Reports to</FieldLabel>
            <NativeSelect
              id="add-de-seat-supervisor"
              disabled={locked}
              value={form.supervisorSeatId}
              onChange={(e) => set({ supervisorSeatId: e.target.value })}
            >
              <NativeSelectOption value="">Choose a seat…</NativeSelectOption>
              {staffed.map((s) => (
                <NativeSelectOption key={s.id} value={s.id}>
                  {seatLabel(s)}
                </NativeSelectOption>
              ))}
            </NativeSelect>
            <FieldDescription>A person holds it: they supervise the DE.</FieldDescription>
          </Field>
        </div>
      ) : picked ? (
        <p className="text-sm text-muted-foreground" data-slot="add-de-reports-to">
          Reports to {supervisor ? seatLabel(supervisor) : "no seat"}.
        </p>
      ) : null}
      {teams.length ? (
        <Field>
          <FieldLabel htmlFor="add-de-team">Team</FieldLabel>
          <NativeSelect
            id="add-de-team"
            disabled={locked}
            value={form.teamId}
            onChange={(e) => set({ teamId: e.target.value })}
          >
            <NativeSelectOption value="">No team</NativeSelectOption>
            {teams.map((t) => (
              <NativeSelectOption key={t.id} value={t.id}>
                {t.name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          <FieldDescription>Optional. The seat joins the team.</FieldDescription>
        </Field>
      ) : null}
      {custom ? (
        <div className="flex flex-col gap-5" data-slot="add-de-custom">
          <Field>
            <FieldLabel htmlFor="add-de-role">What it does</FieldLabel>
            <Textarea
              id="add-de-role"
              rows={2}
              maxLength={2000}
              required
              disabled={locked}
              value={form.role}
              onChange={(e) => set({ role: e.target.value })}
            />
            <FieldDescription>Its role, in a sentence or two.</FieldDescription>
          </Field>
          <Field>
            <FieldLabel htmlFor="add-de-goal">Goal</FieldLabel>
            <Textarea
              id="add-de-goal"
              rows={2}
              maxLength={4000}
              required
              disabled={locked}
              value={form.goal}
              onChange={(e) => set({ goal: e.target.value })}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="add-de-backstory">Backstory</FieldLabel>
            <Textarea
              id="add-de-backstory"
              rows={4}
              maxLength={8000}
              required
              disabled={locked}
              value={form.backstory}
              onChange={(e) => set({ backstory: e.target.value })}
            />
            <FieldDescription>{BACKSTORY_SHAPE_HINT}</FieldDescription>
          </Field>
        </div>
      ) : null}
    </SettingsSection>
  );
}

// 3. Job & standing orders -----------------------------------------------------------------------

export function OrdersField({
  value,
  onChange,
  templateText,
  required,
  readOnly,
  id,
  hint,
}: {
  value: string;
  onChange: (v: string) => void;
  templateText: string | null;
  required: boolean;
  readOnly?: boolean;
  id?: string;
  /** Replaces the required/optional line (the edit mode's own). */
  hint?: string;
}) {
  const operator = useOperatorName();
  const own = useId();
  const fieldId = id ?? own;
  return (
    <Field>
      <FieldLabel htmlFor={fieldId}>Standing orders</FieldLabel>
      <Textarea
        id={fieldId}
        rows={8}
        maxLength={20000}
        readOnly={readOnly}
        value={value}
        placeholder="What it does, what it must never do, how it reports. For example: Source candidates for open roles; never contact a candidate twice in a week."
        onChange={(e) => onChange(e.target.value)}
        data-slot="orders-input"
      />
      <FieldDescription>
        {hint ??
          (required
            ? "Required: given word for word to every worker it starts."
            : `Optional: ${operator}'s template brings its own; yours replace them on its deploy.`)}
        {templateText !== null && value !== templateText && !readOnly ? (
          <>
            {" "}
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0"
              onClick={() => onChange(templateText)}
            >
              Use the template's
            </Button>
          </>
        ) : null}
      </FieldDescription>
    </Field>
  );
}

export function JobStep(props: Parameters<typeof OrdersField>[0]) {
  return (
    <SettingsSection
      icon={ScrollTextIcon}
      title="Job & standing orders"
      description="The rules it always follows. Saved now; a deployed DE gets a change with its next deploy."
    >
      <OrdersField {...props} />
    </SettingsSection>
  );
}

// 4. KPIs ---------------------------------------------------------------------------------------

export function KpiEditor({
  rows,
  onChange,
  metrics,
  removed,
  onRemove,
}: {
  rows: KpiRow[];
  onChange: (rows: KpiRow[]) => void;
  metrics: MetricDef[];
  /** Edit mode: removals are explicit (a KPI left out is never dropped). */
  removed?: string[];
  onRemove?: (id: string) => void;
}) {
  const [pick, setPick] = useState("");
  const chosen = new Set(rows.map((r) => r.metric_def_id));
  const addable = metrics.filter((m) => !m.archived_at && !chosen.has(m.id));
  const setRow = (id: string, patch: Partial<KpiRow>) =>
    onChange(rows.map((r) => (r.metric_def_id === id ? { ...r, ...patch } : r)));
  return (
    <div className="flex flex-col gap-3" data-slot="kpi-editor">
      {rows.length ? (
        <ul className="flex flex-col divide-y divide-border rounded-md border border-border">
          {rows.map((r) => {
            const bad = !targetValid(r.target);
            return (
              <li
                key={r.metric_def_id}
                data-kpi={r.metric_def_id}
                className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2.5"
              >
                <span className="min-w-36 flex-1 text-sm font-medium">{r.name}</span>
                <span className="flex items-center gap-1.5">
                  <Label htmlFor={`kpi-target-${r.metric_def_id}`} className="text-xs">
                    Weekly target
                  </Label>
                  <Input
                    id={`kpi-target-${r.metric_def_id}`}
                    inputMode="decimal"
                    className="h-8 w-24"
                    aria-invalid={bad || undefined}
                    value={r.target}
                    placeholder="None"
                    onChange={(e) => setRow(r.metric_def_id, { target: e.target.value })}
                  />
                </span>
                <span className="flex items-center gap-1.5">
                  <Checkbox
                    id={`kpi-reports-${r.metric_def_id}`}
                    checked={r.reports}
                    onCheckedChange={(v) => setRow(r.metric_def_id, { reports: v === true })}
                  />
                  <Label htmlFor={`kpi-reports-${r.metric_def_id}`} className="text-xs">
                    It reports this
                  </Label>
                  <NativeSelect
                    aria-label={`How often it reports ${r.name}`}
                    className="h-8 w-28"
                    disabled={!r.reports}
                    value={r.duty}
                    onChange={(e) => setRow(r.metric_def_id, { duty: e.target.value as Duty })}
                  >
                    {(Object.keys(DUTY_LABELS) as Duty[]).map((d) => (
                      <NativeSelectOption key={d} value={d}>
                        {DUTY_LABELS[d]}
                      </NativeSelectOption>
                    ))}
                  </NativeSelect>
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Remove ${r.name}`}
                  onClick={() => {
                    onChange(rows.filter((x) => x.metric_def_id !== r.metric_def_id));
                    onRemove?.(r.metric_def_id);
                  }}
                >
                  <Trash2Icon aria-hidden="true" />
                </Button>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground" data-slot="kpis-none">
          No KPIs yet. Add the numbers it is accountable for, or skip this step.
        </p>
      )}
      {removed?.length ? (
        <p className="text-xs text-muted-foreground" data-slot="kpis-removed">
          {removed.length} KPI{removed.length === 1 ? "" : "s"} will be removed from the seat when
          you save.
        </p>
      ) : null}
      <div className="flex flex-wrap items-end gap-2">
        <Field className="min-w-56 flex-1">
          <FieldLabel htmlFor="kpi-add">Add a KPI from the catalog</FieldLabel>
          <NativeSelect id="kpi-add" value={pick} onChange={(e) => setPick(e.target.value)}>
            <NativeSelectOption value="">Choose a metric…</NativeSelectOption>
            {addable.map((m) => (
              <NativeSelectOption key={m.id} value={m.id}>
                {m.name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </Field>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!pick}
          onClick={() => {
            const m = metrics.find((x) => x.id === pick);
            if (!m) return;
            onChange([
              ...rows,
              {
                metric_def_id: m.id,
                name: m.name,
                unit: m.unit,
                target: "",
                reports: false,
                duty: "weekly",
              },
            ]);
            setPick("");
          }}
        >
          <PlusIcon aria-hidden="true" />
          Add
        </Button>
      </div>
      <FieldDescription>
        "It reports this" marks a number the DE reports itself; how often is saved now and becomes
        its reporting schedule once reporting set-up ships.
      </FieldDescription>
    </div>
  );
}

export function KpisStep({
  kpiOn,
  ...props
}: Parameters<typeof KpiEditor>[0] & { kpiOn: boolean }) {
  return (
    <SettingsSection
      icon={GaugeIcon}
      title="KPIs"
      description="The numbers its seat is measured on, with weekly targets. They apply as soon as they are saved."
    >
      {kpiOn ? (
        <KpiEditor {...props} />
      ) : (
        <Notice tone="neutral" data-slot="kpis-off">
          KPIs need the KPI add-on. You can add them later from the DE's profile.
        </Notice>
      )}
    </SettingsSection>
  );
}

// 5. Models & budget ----------------------------------------------------------------------------

export function ModelsEditor({
  rows,
  onChange,
  required,
}: {
  rows: ModelRow[];
  onChange: (rows: ModelRow[]) => void;
  required: boolean;
}) {
  const operator = useOperatorName();
  const ids = useId();
  const setRow = (key: number, patch: Partial<ModelRow>) =>
    onChange(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  return (
    <fieldset className="flex flex-col gap-2" aria-describedby={ids} data-slot="models-editor">
      <legend className="text-sm font-medium">Models</legend>
      <p id={ids} className="text-sm text-muted-foreground">
        {required
          ? "Each role's model, through your company's gateway and inside its model policy."
          : `Optional: ${operator}'s template sets the models; a role you set here replaces its model.`}
      </p>
      {rows.map((r) => (
        <div key={r.key} className="flex flex-wrap items-end gap-2" data-slot="model-row">
          <Field className="w-36">
            <FieldLabel htmlFor={`${ids}-role-${r.key}`}>Role</FieldLabel>
            <Input
              id={`${ids}-role-${r.key}`}
              value={r.role}
              onChange={(e) => setRow(r.key, { role: e.target.value })}
            />
          </Field>
          <Field className="min-w-40 flex-1">
            <FieldLabel htmlFor={`${ids}-model-${r.key}`}>Model</FieldLabel>
            <Input
              id={`${ids}-model-${r.key}`}
              className="font-mono"
              value={r.model}
              onChange={(e) => setRow(r.key, { model: e.target.value })}
            />
          </Field>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`Remove the ${r.role || "new"} role`}
            onClick={() => onChange(rows.filter((x) => x.key !== r.key))}
          >
            <Trash2Icon aria-hidden="true" />
          </Button>
        </div>
      ))}
      <div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onChange([...rows, modelRow(rows.length ? "" : "lead", "")])}
        >
          <PlusIcon aria-hidden="true" />
          Add a role
        </Button>
      </div>
    </fieldset>
  );
}

export function BudgetField({
  amount,
  onChange,
  currency,
  month,
  existing,
}: {
  amount: string;
  onChange: (v: string) => void;
  currency: string;
  month: string;
  /** The seat's own budget, when it has one (then nothing is asked). */
  existing: string | null;
}) {
  return existing ? (
    <Notice tone="neutral" data-slot="seat-has-budget">
      The seat already has a {existing} budget; it applies to this DE.
    </Notice>
  ) : (
    <Field>
      <FieldLabel htmlFor="add-de-budget">Monthly budget ({currency})</FieldLabel>
      <Input
        id="add-de-budget"
        inputMode="decimal"
        className="w-40 max-w-full self-start"
        value={amount}
        onChange={(e) => onChange(e.target.value)}
      />
      <FieldDescription>
        For {month}; change it or add a hard cap later in Setup › Budgets. Every DE needs one.
      </FieldDescription>
    </Field>
  );
}

export function ModelsStep({
  models,
  budget,
}: {
  models: Parameters<typeof ModelsEditor>[0];
  budget: Parameters<typeof BudgetField>[0];
}) {
  return (
    <SettingsSection icon={WalletIcon} title="Models & budget">
      <ModelsEditor {...models} />
      <BudgetField {...budget} />
    </SettingsSection>
  );
}

// 6. Channels & access --------------------------------------------------------------------------

export type JoinableChannel = {
  id: string;
  name: string;
  /** "#operations", "#operations · Northwind Ops" when another space has one by that name. */
  label: string;
};

export function ChannelsFields({
  channels,
  joined,
  home,
  onHome,
  joins,
  onJoins,
  postApproval,
  onPostApproval,
  approver,
  onApprover,
  approverSeats,
  supervisorLabel,
}: {
  /** The channels it may join (null: Chat is off). */
  channels: JoinableChannel[] | null;
  /**
   * Edit mode: the channels it is in now (shown, and ticked and locked in "It also joins");
   * null while they load. Left out on create (a new DE is in none).
   */
  joined?: JoinableChannel[] | null | undefined;
  home: string;
  onHome: (id: string) => void;
  joins: string[];
  onJoins: (ids: string[]) => void;
  postApproval: boolean;
  onPostApproval: (v: boolean) => void;
  approver: string;
  onApprover: (id: string) => void;
  approverSeats: ChartSeat[];
  supervisorLabel: string;
}) {
  return (
    <>
      {channels ? (
        <>
          <Field>
            <FieldLabel htmlFor="add-de-home">Home channel</FieldLabel>
            <NativeSelect id="add-de-home" value={home} onChange={(e) => onHome(e.target.value)}>
              <NativeSelectOption value="">No home channel</NativeSelectOption>
              {channels.map((c) => (
                <NativeSelectOption key={c.id} value={c.id}>
                  {c.label}
                </NativeSelectOption>
              ))}
            </NativeSelect>
            <FieldDescription>Where people find it and @-mention it. It joins it.</FieldDescription>
          </Field>
          {joined !== undefined ? (
            <p className="text-sm" data-slot="de-joined-channels">
              <span className="font-medium">It is in: </span>
              {joined === null
                ? "Loading its channels…"
                : joined.length
                  ? joined.map((c) => c.label).join(", ")
                  : "No channel yet"}
            </p>
          ) : null}
          <fieldset className="flex flex-col gap-2" data-slot="add-de-joins">
            <legend className="text-sm font-medium">It also joins</legend>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              {channels
                .filter((c) => c.id !== home)
                .map((c) => {
                  const member = Boolean(joined?.some((j) => j.id === c.id));
                  return (
                    <span key={c.id} className="flex items-center gap-1.5">
                      <Checkbox
                        id={`join-${c.id}`}
                        checked={member || joins.includes(c.id)}
                        disabled={member}
                        onCheckedChange={(v) =>
                          onJoins(v === true ? [...joins, c.id] : joins.filter((x) => x !== c.id))
                        }
                      />
                      <Label htmlFor={`join-${c.id}`}>
                        {c.label}
                        {member ? <span className="text-muted-foreground"> (joined)</span> : null}
                      </Label>
                    </span>
                  );
                })}
            </div>
          </fieldset>
        </>
      ) : (
        <Notice tone="neutral" data-slot="chat-off">
          Chat is off for this company: no channels to join.
        </Notice>
      )}
      <div className="flex flex-col gap-2">
        <span className="flex items-center gap-2">
          <Checkbox
            id="add-de-approval"
            checked={postApproval}
            onCheckedChange={(v) => onPostApproval(v === true)}
          />
          <Label htmlFor="add-de-approval">Its chat posts wait for approval</Label>
        </span>
        {postApproval ? (
          <Field>
            <FieldLabel htmlFor="add-de-approver">Approver</FieldLabel>
            <NativeSelect
              id="add-de-approver"
              className="max-w-md"
              value={approver}
              onChange={(e) => onApprover(e.target.value)}
            >
              <NativeSelectOption value="">Its supervisor ({supervisorLabel})</NativeSelectOption>
              {approverSeats.map((s) => (
                <NativeSelectOption key={s.id} value={s.id}>
                  {seatLabel(s)}
                </NativeSelectOption>
              ))}
            </NativeSelect>
            <FieldDescription>
              A post becomes an approval card for this seat; it is posted once approved.
            </FieldDescription>
          </Field>
        ) : null}
      </div>
    </>
  );
}

export function ChannelsStep(props: Parameters<typeof ChannelsFields>[0]) {
  return (
    <SettingsSection icon={MessagesSquareIcon} title="Channels & access">
      <ChannelsFields {...props} />
    </SettingsSection>
  );
}
